// @flow

import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Node, RefObject } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { CloseIcon, PhotoIcon, LensIcon, DeleteIcon } from '@riseart/icons';
import { Modal } from '@riseart/antd-provider';
import { Button, Heading, Image, Link, MediaQuery, Paragraph } from '@riseart/common';
import { ContinuousSlider } from '@riseart/slider';
import { useUploadedFiles, FileUploader } from '@riseart/files';
import { isTouchDevice, useAclAccess } from '@riseart/fe-utils';
import {
  application as CONFIG_APP,
  components as CONFIG_COMPONENTS,
  file as CONFIG_FILE,
} from 'Config';
import { file as ENUM_FILE, errors as ENUM_ERRORS } from 'Enum';
import { FileUploadInfo, FileUploadInfoItem } from 'shared_components/common/file/UploadInfo';
import { useQuery } from 'shared_services/apollo/useQuery';
import { delay, actionFactory } from 'shared_services/riseart/utils/Utils';
import { errorAdd } from 'shared_services/redux/actions/errors/errors';
import { selectAclRole } from 'shared_services/redux/selectors/auth';
import { selectUserId } from 'shared_services/redux/selectors/user';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { HelpCard } from 'shared_components/common/HelpCard';
import { FileDropzone } from 'shared_components/common/file/Dropzone';
import { createFileUpload, updateFileUpload } from 'shared_data/handlers';
import { ConfirmationModal } from 'shared_components/common/modal/Confirmation';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { GUI_PROPERTIES } from 'shared_models/Gui';
import { guiUpdate } from 'shared_services/redux/actions/application/gui';
import { GOOGLE_TAGMANAGER_ACTIONS } from 'shared_data/providers/google/tagmanager/Actions';
import LIST_IMAGE_UPLOADS from 'shared_data/queries/file/listImageUploads.graphql';
import DELETE_IMAGE_UPLOAD from 'shared_data/queries/file/deleteImageUpload.graphql';

import {
  visualSearchCls,
  visualSearchHeadingCls,
} from 'shared_components/forms/search/visual/Visual.less';
import {
  lensSearchCls,
  lensSearchContainerCls,
  lensSearchContainerLeftCls,
  lensSearchContainerRightCls,
  lensSearchParagraphCls,
  lensSearchMessageCls,
  lensSearchSliderCls,
  lensSearchSliderItemCls,
  lensSearchSliderItemSelectedCls,
  lensSearchSliderItemButtonCls,
  lensSearchSliderImageCls,
  lensSearchSliderHoverCls,
} from 'shared_components/forms/search/lens/Section.less';
import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';

export type ImageType = 'user';

type Props = {
  title?: Node | string,
  initialImageId?: number | null | void,
  loading: boolean,
  assembleUrl?: Function,
};

/**
 * LensSection
 *
 * @param {props} Props
 * @returns {Node}
 */
export function LensSection({
  title,
  initialImageId,
  loading: parentLoading = false,
  assembleUrl,
}: Props): Node {
  // Constants
  const ITEMS_PER_PAGE = 15;
  const { lens: LENS_ACL_ROLE } = CONFIG_APP.acl.resourcePermissions;
  const { imageUpload: IMAGE_UPLOAD_ENUM } = ENUM_FILE;
  const { imageUpload: IMAGE_UPLOAD_CONFIG } = CONFIG_FILE;

  // Helpers
  const { formatMessage } = useIntl();
  const dispatch: Function = useDispatch();
  const userId = useSelector(selectUserId);
  const history = useHistory();
  const aclRole = useSelector(selectAclRole);
  const { isAllowed } = useAclAccess({
    role: aclRole,
    resourcePermissions: LENS_ACL_ROLE,
    rolesHierarchy: CONFIG_APP.acl.rolesHierarchy,
  });
  const timeoutId: RefObject<TimeoutID | null> = useRef(null);
  const lastSearchedImage: RefObject<number | null> = useRef(null);
  const fileUploader: RefObject<FileUploader | null> = useRef(null);
  const resetImageList: RefObject<boolean> = useRef(true);
  const sliderRef: RefObject<ContinuousSlider | null> = useRef(null);
  const imageToDelete: RefObject<string | number | null> = useRef(null);

  // State
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lensImageId, setLensImageId] = useState<number | string | null>(initialImageId || null);
  const [imageUploadsList, setImageUploadsList] = useState<[] | null>(null);

  // Delete Mutation
  const [deleteImage, { loading: deleteLoading }] = useMutation(DELETE_IMAGE_UPLOAD);

  // Fetch data
  const {
    loading: fetchImagesLoading,
    data,
    refetch: refetchImageUploadsList,
  } = useQuery(LIST_IMAGE_UPLOADS, {
    skip: !isAllowed || !userId,
    fetchPolicy: 'network-only',
    variables: {
      sort: 'id',
      order: 'DESC',
      items: ITEMS_PER_PAGE,
      page: currentPage,
      sign: true,
      hierarchy: false,
      filters: {
        type: { value: IMAGE_UPLOAD_ENUM.type.TYPE_VISUAL_SEARCH_USER_SQUARE },
        objectType: { value: IMAGE_UPLOAD_ENUM.object.type.USER },
        objectId: { value: userId ? `${userId}` : null },
      },
    },
  });
  const { items: imagesList, collection } = (data && data.listImageUploads) || {};
  const hasNextPage = collection && collection.items === ITEMS_PER_PAGE;

  /**
   * handleUrlChange
   *
   * @param {number} imageId
   */
  const handleUrlChange = useCallback(
    (imageId: number | string) => {
      history.push(
        typeof assembleUrl === 'function'
          ? assembleUrl(imageId)
          : UrlAssembler.artList({ search: { l: imageId } }),
      );
    },
    [history, assembleUrl],
  );

  useEffect(() => {
    if (lensImageId && imageUploadsList && lastSearchedImage.current !== lensImageId) {
      // Extract data for lens image by id or parentId
      const foundLensImage = imageUploadsList.find(
        ({ id, parentId }) => [id, parentId].indexOf(lensImageId) > -1,
      );
      if (foundLensImage) {
        // Store lensImageId in component
        lastSearchedImage.current = lensImageId;

        // Dispatch action
        dispatch(
          actionFactory(GOOGLE_TAGMANAGER_ACTIONS.LENS_SEARCH_SEARCH_EXECUTE, foundLensImage),
        );
      }
    }
  }, [lensImageId, imageUploadsList, dispatch]);

  // File uploads
  const {
    uploadedFiles,
    setUploadedFiles,
    handleFileDrop: handleFileDropCallback,
    handleFileValidation,
  } = useUploadedFiles({
    filesByType: { [IMAGE_UPLOAD_ENUM.category.VISUAL_SEARCH_USER]: [] },
    maxFileSize: IMAGE_UPLOAD_CONFIG.maxFilesize,
    sizeValidationMessage: formatMessage(
      { id: 'forms.common.largerFilesize' },
      { maxFilesize: IMAGE_UPLOAD_CONFIG.maxFilesize },
    ),
  });
  const actionErrorAdd = useCallback(
    (error: string, file: Record<string, any>) =>
      dispatch(
        errorAdd({
          detail: `${file && file.path ? `[${file.path}] ` : ''}${error}`,
          level: ENUM_ERRORS.levels.WARNING,
          expire: 20,
        }),
      ),
    [dispatch],
  );

  /**
   * handleImageClick
   *
   * @param {Object} item
   * @returns {Function}
   */
  const handleImageClick =
    (item: Object): Function =>
    () => {
      resetImageList.current = false;
      // Use parentId if existing or id
      handleUrlChange(item.parentId || item.id);
    };

  /**
   * Load initial image list
   */
  useEffect(() => {
    if (!imageUploadsList && imagesList) {
      setImageUploadsList(imagesList);
    }
  }, [imageUploadsList, imagesList]);

  /**
   * Reset slider to first slide when page is set to 1
   */
  useEffect(() => {
    if (currentPage === 1 && sliderRef.current) {
      sliderRef.current.scrollToStart();
    }
  }, [currentPage]);

  useEffect(() => {
    if (resetImageList.current) {
      // Reset image list
      setCurrentPage(1);
      setImageUploadsList(null);

      if (imagesList) {
        refetchImageUploadsList({ page: 1 }).then((response) => {
          const { items: imagesList } =
            (response && response.data && response.data.listImageUploads) || {};
          setImageUploadsList(imagesList);
        });
      }
    } else {
      resetImageList.current = true;
    }

    // Set lens image id in state
    setLensImageId(initialImageId || null);
  }, [initialImageId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Accumulate image upload items loaded from next page
   */
  useEffect(() => {
    if (
      !fetchImagesLoading &&
      imageUploadsList &&
      imageUploadsList.length &&
      imagesList &&
      imagesList.length &&
      currentPage > 1
    ) {
      setImageUploadsList((currentList) => [...(currentList || []), ...imagesList]);
    }
  }, [currentPage, fetchImagesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * handleModalClose
   */
  const handleModalClose = () => {
    setOpenModal(false);

    if (fileUploader.current instanceof FileUploader) {
      fileUploader.current.stopPolling();
    }

    setUploadedFiles({ [IMAGE_UPLOAD_ENUM.category.VISUAL_SEARCH_USER]: [] });
    clearTimeout(timeoutId.current);
    setLoading(false);
  };

  /**
   * handleFileUpload
   *
   * @param {Record<string, any>[]} files
   * @param {ImageType} type
   * @param {(state: Record<string, any> | null) => () => void} callback
   * @returns {void}
   */
  const handleFileUpload = (
    files: Record<string, any>[],
    type: ImageType,
    callback: (state: Record<string, any> | null) => () => void,
  ) => {
    setLoading(true);
    setOpenModal(true);
    fileUploader.current = new FileUploader(
      FileUploader.flattenFiles({ [type]: files }),
      userId,
      ENUM_FILE.object.type.IMAGE_UPLOAD,
      createFileUpload,
      updateFileUpload,
      actionErrorAdd,
    );
    fileUploader.current.subscribe(FileUploader.eventsTypes.CREATE_UPLOAD, (transactionState) => {
      dispatch(actionFactory(GOOGLE_TAGMANAGER_ACTIONS.LENS_SEARCH_UPLOAD_START, transactionState));
    });
    fileUploader.current.subscribe(FileUploader.eventsTypes.UPDATE, (fileUploadState) => {
      callback(fileUploadState.files);
    });
    fileUploader.current.subscribe(FileUploader.eventsTypes.ERROR, () => setLoading(false));
    fileUploader.current.subscribe(FileUploader.eventsTypes.COMPLETE, (fileUploadState) => {
      // Trigger GTM action for completed upload
      dispatch(
        actionFactory(GOOGLE_TAGMANAGER_ACTIONS.LENS_SEARCH_UPLOAD_COMPLETE, fileUploadState),
      );

      // Refetch images list with delay
      delay(() => {
        setOpenModal(false);

        refetchImageUploadsList({ page: 1 })
          .then((response) => {
            callback(fileUploadState.files);
            handleUrlChange(
              response.data.listImageUploads.items[0].parentId ||
                response.data.listImageUploads.items[0].id,
            );
            handleModalClose();
          })
          .catch(() => callback(null))
          .finally(() => {
            setLoading(false);
          });
      }, CONFIG_COMPONENTS.search.lens.overlay.delay);
    });
    fileUploader.current.process();
  };

  /**
   * handleFileDrop
   *
   * @param {ImageType} type
   * @returns {(files: Record<string, any>[]) => void}
   */
  const handleFileDrop = (type: ImageType) => (files: Record<string, any>[]) => {
    handleFileUpload(files, type, handleFileDropCallback(type));
  };

  /**
   * handleSlide
   *
   * @param {Object} Slider properties
   */
  const handleSlide = useCallback(
    ({ containerWidth, trackWidth, scrollLeft }: Object) => {
      if (
        hasNextPage &&
        !fetchImagesLoading &&
        containerWidth > trackWidth + (scrollLeft - containerWidth)
      ) {
        setCurrentPage((currentPage) => currentPage + 1);
      }
    },
    [hasNextPage, fetchImagesLoading],
  );

  /**
   * handleOpenLensOverlay
   */
  const handleOpenLensOverlay = useCallback(
    () => dispatch(guiUpdate(GUI_PROPERTIES.LENS_SEARCH, { opened: true })),
    [dispatch],
  );

  /**
   * handleImageDelete
   *
   * @param {Function} handleConfirmationClose
   * @returns {void}
   */
  const handleImageDelete = useCallback(
    (handleConfirmationClose: Function): void => {
      deleteImage({ variables: { id: imageToDelete.current } })
        .then(() => {
          if (imageUploadsList) {
            setImageUploadsList(
              imageUploadsList.filter(({ parentId }) => parentId !== imageToDelete.current),
            );

            // Update the page if the deleted image is current selected image
            if (lensImageId === imageToDelete.current) {
              handleUrlChange('');
            }
          }
        })
        .catch(() => null)
        .finally(() => {
          // Clean imageToDelete
          imageToDelete.current = null;
        });

      handleConfirmationClose();
    },
    [deleteImage, imageUploadsList, lensImageId, handleUrlChange],
  );

  /**
   * handleConfirmationOpen
   *
   * @param {string | number} id
   * @param {Function} handleOpen
   * @returns {Function}
   */
  const handleConfirmationOpen = (id: string | number, handleOpen: Function) => (e: Event) => {
    e.stopPropagation();

    imageToDelete.current = id;
    handleOpen();
  };

  if (!isAllowed) {
    const lensUrl = UrlAssembler.artList({ search: { l: lensImageId } });

    return (
      <section className={classNames(visualSearchCls, lensSearchCls)}>
        <Paragraph className={lensSearchMessageCls}>
          <FormattedMessage
            id="forms.search.lens.authMessage"
            values={{
              signin: (chunk) => (
                <Link
                  title={chunk}
                  to={UrlAssembler.byRouteKey('login', {
                    search: { forward: lensUrl, role: LENS_ACL_ROLE },
                  })}
                >
                  {chunk}
                </Link>
              ),
              signup: (chunk) => (
                <Link
                  title={chunk}
                  to={UrlAssembler.byRouteKey('register', {
                    search: { forward: lensUrl, role: LENS_ACL_ROLE },
                  })}
                >
                  {chunk}
                </Link>
              ),
            }}
          >
            {(chunk) => chunk}
          </FormattedMessage>
        </Paragraph>
      </section>
    );
  }

  return (
    <section className={classNames(visualSearchCls, lensSearchCls)}>
      <IsomorphicRipple isActive={parentLoading || loading || fetchImagesLoading || deleteLoading}>
        {title ? (
          <Heading level="5" tag="h3" className={visualSearchHeadingCls}>
            {title}
          </Heading>
        ) : null}
        <div className={lensSearchContainerCls}>
          <div className={lensSearchContainerRightCls}>
            <ConfirmationModal
              title={formatMessage({ id: 'forms.search.lens.deleteImage.title' })}
              loading={deleteLoading}
              body={() => (
                <Paragraph>
                  {formatMessage({ id: 'forms.search.lens.deleteImage.description' })}
                </Paragraph>
              )}
              onConfirm={handleImageDelete}
            >
              {({ handleOpen }) => (
                <ContinuousSlider
                  ref={sliderRef}
                  height={155}
                  nextArrowText="common.next"
                  previousArrowText="common.previous"
                  className={classNames(
                    lensSearchSliderCls,
                    !isTouchDevice() && lensSearchSliderHoverCls,
                  )}
                  onSlide={handleSlide}
                >
                  {(imageUploadsList &&
                    imageUploadsList.map((image: Object) => (
                      // eslint-disable-next-line
                      <div
                        key={image.id}
                        role="button"
                        className={classNames(
                          lensSearchSliderItemCls,
                          ((image.parentId && lensImageId === image.parentId) ||
                            (image.parentId && image.id && lensImageId === image.id)) &&
                            lensSearchSliderItemSelectedCls,
                        )}
                        onClick={handleImageClick(image)}
                      >
                        <Image
                          className={lensSearchSliderImageCls}
                          src={image.signedUri}
                          alt={`${image.id}`}
                        />
                        <Button
                          type="primary"
                          shape="circle"
                          size="small"
                          className={lensSearchSliderItemButtonCls}
                          onClick={handleConfirmationOpen(image.parentId || image.id, handleOpen)}
                        >
                          <DeleteIcon />
                        </Button>
                      </div>
                    ))) || <div />}
                </ContinuousSlider>
              )}
            </ConfirmationModal>
          </div>
          <div className={lensSearchContainerLeftCls}>
            <MediaQuery minWidth={raScreenLg}>
              {(isDesktop) =>
                isDesktop ? (
                  <HelpCard>
                    <FileDropzone
                      onDrop={(files) => {
                        handleFileDrop(IMAGE_UPLOAD_ENUM.category.VISUAL_SEARCH_USER)(files);
                      }}
                      validator={handleFileValidation}
                      accept={IMAGE_UPLOAD_CONFIG.acceptedFiles}
                      multiple={false}
                    >
                      <PhotoIcon />
                      <br />
                      <Paragraph className={lensSearchParagraphCls}>
                        <FormattedMessage id="forms.search.lens.help">
                          {(text) => text}
                        </FormattedMessage>
                      </Paragraph>
                      <Button type="secondary" size="small">
                        <FormattedMessage id="components.fileDropzone.button">
                          {(text) => text}
                        </FormattedMessage>
                      </Button>
                    </FileDropzone>
                  </HelpCard>
                ) : (
                  <Button
                    type="secondary"
                    size="small"
                    icon={<LensIcon />}
                    onClick={handleOpenLensOverlay}
                  >
                    <FormattedMessage id="forms.search.lens.overlay.takePhoto">
                      {(text) => (
                        <React.Fragment>
                          &nbsp;&nbsp;
                          {text}
                        </React.Fragment>
                      )}
                    </FormattedMessage>
                  </Button>
                )
              }
            </MediaQuery>
          </div>
        </div>
        {uploadedFiles &&
        uploadedFiles[IMAGE_UPLOAD_ENUM.category.VISUAL_SEARCH_USER] &&
        uploadedFiles[IMAGE_UPLOAD_ENUM.category.VISUAL_SEARCH_USER].length &&
        openModal ? (
          <Modal
            open
            centered
            onCancel={handleModalClose}
            closeIcon={<CloseIcon />}
            title={formatMessage({ id: 'components.fileDropzone.dialogTitle' })}
            footer={null}
          >
            <FileUploadInfo>
              {uploadedFiles[IMAGE_UPLOAD_ENUM.category.VISUAL_SEARCH_USER].map(
                (
                  { name, size, progress, status, errorMessage }: Record<string, any>,
                  idx: number,
                ) => (
                  <FileUploadInfoItem
                    key={`${name}${idx}`}
                    name={name}
                    size={size}
                    progress={progress}
                    status={status}
                    errorMessage={errorMessage}
                  />
                ),
              )}
            </FileUploadInfo>
          </Modal>
        ) : null}
      </IsomorphicRipple>
    </section>
  );
}
